import { useEffect, useState } from "react";
import { Button, Group, Modal, Stack, Switch, Table, Text, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../../api/client";
import type { ExpenseCategory } from "../../api/types";
import { ResponsiveTable } from "../responsiveTable/ResponsiveTable";

export function ExpenseCategoriesModal({
  opened,
  onClose,
  onChanged,
}: {
  opened: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      setCategories(await api.listExpenseCategories());
    } catch (err) {
      notifications.show({ color: "red", message: err instanceof ApiError ? JSON.stringify(err.detail) : String(err) });
    }
  }

  useEffect(() => {
    if (opened) void load();
  }, [opened]);

  async function add() {
    const v = name.trim();
    if (!v) return;
    setSaving(true);
    try {
      await api.createExpenseCategory(v);
      setName("");
      await load();
      onChanged();
    } catch (err) {
      notifications.show({ color: "red", message: err instanceof ApiError ? JSON.stringify(err.detail) : String(err) });
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(c: ExpenseCategory) {
    try {
      await api.updateExpenseCategory(c.id, { active: !c.active });
      await load();
      onChanged();
    } catch (err) {
      notifications.show({ color: "red", message: err instanceof ApiError ? JSON.stringify(err.detail) : String(err) });
    }
  }

  async function remove(c: ExpenseCategory) {
    if (!window.confirm(t("expenses.confirmDeleteCategory", { name: c.name }))) return;
    try {
      await api.deleteExpenseCategory(c.id);
      await load();
      onChanged();
    } catch (err) {
      const inUse = err instanceof ApiError && (err.detail as { error?: string } | null)?.error === "category_in_use";
      notifications.show({
        color: "red",
        message: inUse ? t("expenses.categoryInUse") : err instanceof ApiError ? JSON.stringify(err.detail) : String(err),
      });
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t("expenses.categoriesTitle")} centered>
      <Stack>
        <Group align="end" gap="xs">
          <TextInput
            label={t("expenses.categoryName")}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void add(); }}
            style={{ flex: 1 }}
          />
          <Button leftSection={<IconPlus size={14} />} loading={saving} onClick={() => void add()}>
            {t("common.add")}
          </Button>
        </Group>
        {categories.length === 0 ? (
          <Text c="dimmed" size="sm">{t("expenses.noCategories")}</Text>
        ) : (
          <ResponsiveTable
            mobile={{
              rows: categories,
              rowKey: (c) => c.id,
              title: (c) => c.name,
              alwaysOpen: true,
              fields: [
                {
                  label: t("common.active"),
                  render: (c) => (
                    <Switch
                      checked={c.active}
                      onChange={() => void toggleActive(c)}
                      label={c.active ? t("common.active") : t("common.inactive")}
                      size="xs"
                    />
                  ),
                },
              ],
              actions: (c) => (
                <Button size="compact-md" variant="subtle" color="red" onClick={() => void remove(c)}>
                  <IconTrash size={16} />
                </Button>
              ),
            }}
          >
          <Table>
            <Table.Tbody>
              {categories.map((c) => (
                <Table.Tr key={c.id}>
                  <Table.Td>{c.name}</Table.Td>
                  <Table.Td w={120}>
                    <Switch
                      checked={c.active}
                      onChange={() => void toggleActive(c)}
                      label={c.active ? t("common.active") : t("common.inactive")}
                      size="xs"
                    />
                  </Table.Td>
                  <Table.Td w={40}>
                    <Button size="compact-xs" variant="subtle" color="red" onClick={() => void remove(c)}>
                      <IconTrash size={14} />
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          </ResponsiveTable>
        )}
      </Stack>
    </Modal>
  );
}
